import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Accordion, AccordionBody, AccordionHeader, AccordionItem } from 'reactstrap';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { useTenantContext } from '../../auth/useTenantContext';
import { useRole } from '../../auth/useRole';
import { usePickListDetail, usePickListCustomers, pickListHasPartials } from '../../api/pickLists';
import {
  LifecycleApiError, useConfirmPickList, useReopenPickList,
  useReassignMission, useUnassignMission, useAssignMission,
  useCloseMission,
  useResetPickListSplit, useEditItemPicked,
  downloadPartialsCsv, useSetColliImballo} from '../../api/pickListLifecycle';
import { useAssignmentBoard, useAssignWhole } from '../../api/assignments';
import { PickListCustomerFilter } from './PickListCustomerFilter';
import { PickListAssignPanel } from './PickListAssignPanel';
import { Button } from 'reactstrap';
import { PickListItemsSection } from './PickListItemsSection';
import { PickListMissionsSection } from './PickListMissionsSection';
import { PickListAutoSplitBanner } from './PickListAutoSplitBanner';
import { PickListSplitWizard } from './PickListSplitWizard';
import { PickListSignoffBar } from './PickListSignoffBar';
import { PickListColliImballoBar } from './PickListColliImballoBar';
import { PickListManualFulfillBar } from './PickListManualFulfillBar';
import { ConfirmDialog } from './ConfirmDialog';
import { ReopenDialog } from './ReopenDialog';
import { usePickListLiveRefresh } from '../../hooks/usePickListLiveRefresh';

export interface PickListDetailPanelProps {
  id: number;
}

/**
 * Full pick-list detail content. Extracted from the old PickListDetail page so
 * it can be embedded inline in the PickListsList accordion (SP-5l). All data
 * fetching, sign-off, split-wizard and live-refresh logic is unchanged; this
 * adds the level-2 "Cliente" (recipient) grouping.
 */
export function PickListDetailPanel({ id }: PickListDetailPanelProps) {
  const { t } = useTranslation('picklist');
  const { token, tenantCode } = useTenantContext();
  const qc = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  // SP-8 — toast post-conferma: visualizza un Alert temporaneo dopo
  // confirm() OK. `confirmFlash.kind='partial'` indica conferma con
  // inevasi (e.g. il CSV è stato scaricato); `kind='full'` = prelievo
  // completo (nessun CSV da scaricare).
  const [confirmFlash, setConfirmFlash] = useState<
    { kind: 'partial' | 'full'; csvDownloaded: boolean } | null
  >(null);
  // SP-7c — su mobile, le 4 sezioni del dettaglio (clienti/articoli/missioni/
  // audit) sono wrappate in un sub-accordion. Default open: solo Articoli.
  // L'utente può chiudere tutto (string vuota) o aprire un'altra sezione.
  const isMobile = useIsMobile();
  const [openSection, setOpenSection] = useState<string>('items');
  // SP-6j — customer filter: radio singolo per la tupla (cliente, destinatario).
  // `null` = nessuna selezione → tutti gli articoli visibili (default all'apertura).
  const [selectedCustomer, setSelectedCustomer] =
    useState<{ customer_code: string; recipient_code: number | null } | null>(null);

  const { isTenantAdmin, isWarehouseAdmin, isPlatformAdmin } = useRole();
  const canMutate = isTenantAdmin || isWarehouseAdmin;
  const [reassignError, setReassignError] = useState<string | null>(null);
  // SP-8d — pannello inline "Assegna lista" (solo liste open, via assign-whole).
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  // SP-6f: state for "Ripristina lista" error surfaced in the dialog
  // (declared up here to respect React hook order; handler+mapper below).
  const [resetSplitError, setResetSplitError] = useState<string | null>(null);

  const { data, isLoading, isError } = usePickListDetail(token, tenantCode, id);
  // SP-11b: auto-refresh dei clienti finché la lista è "viva" (allineato al
  // polling del dettaglio in usePickListDetail).
  const customersQ = usePickListCustomers(token, tenantCode, id, data?.pick_list.status === 'in_progress');
  // SP-6j — niente più auto-seed di "tutti selezionati": default = null = tutto visibile.

  const warehouseHint = data?.pick_list.warehouse_id;
  usePickListLiveRefresh(token, tenantCode, id, warehouseHint ?? null);
  const confirmM = useConfirmPickList(token, tenantCode, warehouseHint);
  const setColliImballoM = useSetColliImballo(token, tenantCode, warehouseHint);
  const reopenM = useReopenPickList(token, tenantCode, warehouseHint);
  // SP-5p: reassign/unassign from the pick-list detail.
  const reassignM = useReassignMission(token, tenantCode, warehouseHint);
  const unassignM = useUnassignMission(token, tenantCode, warehouseHint);
  const assignM = useAssignMission(token, tenantCode, warehouseHint);
  const closeMissionM = useCloseMission(token, tenantCode, warehouseHint);
  const resetSplitM = useResetPickListSplit(token, tenantCode, warehouseHint);
  const editPickedM = useEditItemPicked(token, tenantCode, warehouseHint);
  // SP-8d — assegnazione whole-list inline (lista open → 1 missione assegnata).
  const assignWholeM = useAssignWhole(token, tenantCode, { warehouseId: warehouseHint ?? -1 });
  // Operators of this warehouse for the reassign picker (reuse the board source).
  const boardQ = useAssignmentBoard(token, tenantCode, warehouseHint ?? null);

  // SP-8 — auto-dismiss del flash post-conferma dopo 8s. Posizionato PRIMA
  // degli early-return (isLoading/isError) per rispettare le Rules of Hooks:
  // l'effect deve essere chiamato in ogni render, indipendentemente dallo
  // stato di caricamento.
  useEffect(() => {
    if (confirmFlash == null) return;
    const handle = window.setTimeout(() => setConfirmFlash(null), 8000);
    return () => window.clearTimeout(handle);
  }, [confirmFlash]);

  // Clienti della lista (alimentano la tabella radio in-accordion).
  // DEVE essere calcolato PRIMA degli early return (Rules of Hooks).
  // SP-8b: i filtri testo Cliente sono ora globali in sidebar (PickListHeadFilters),
  // non più qui — la tabella radio mostra tutti i clienti della lista.
  const customers = useMemo(() => customersQ.data ?? [], [customersQ.data]);
  const hasCustomers = customers.length > 0;

  if (isLoading) return <p className="text-muted m-0">{t('loading')}</p>;
  if (isError || !data) {
    return <p className="text-muted m-0">{t('detail_unavailable')}</p>;
  }

  function refresh(): void {
    qc.invalidateQueries({ queryKey: ['admin', 'pick-lists', 'detail', id] });
    qc.invalidateQueries({ queryKey: ['admin', 'audit', 'tenant'] });
  }

  // SP-5p — map the lifecycle API errors to Italian operator-facing messages.
  function mapMissionError(e: unknown): string {
    if (e instanceof LifecycleApiError) {
      switch (e.code) {
        case 'mission_invalid_state':         return t('errors.mission_invalid_state');
        case 'operator_not_assignable':       return t('errors.operator_not_assignable');
        case 'mission_already_reassigned':    return t('errors.mission_already_reassigned');
        case 'warehouse_admin_grant_required':return t('errors.warehouse_admin_grant_required');
        case 'reason_required':               return t('errors.reason_required');
      }
      if (e.status === 404) return t('errors.mission_not_found');
    }
    return (e as Error)?.message ?? t('errors.unknown');
  }

  async function handleReassign(vars: { missionId: number; newOperatorUserId: number }): Promise<void> {
    setReassignError(null);
    try {
      await reassignM.mutateAsync(vars);
    } catch (e) {
      setReassignError(mapMissionError(e));
      throw e; // keep the dialog open
    }
  }

  async function handleUnassign(missionId: number): Promise<void> {
    try {
      await unassignM.mutateAsync({ missionId });
    } catch (e) {
      window.alert(mapMissionError(e));
    }
  }

  async function handleCloseMission(missionId: number): Promise<void> {
    try {
      await closeMissionM.mutateAsync({ missionId });
    } catch (e) {
      window.alert(mapMissionError(e));
    }
  }

  async function handleAssign(vars: { missionId: number; operatorUserId: number }): Promise<void> {
    setReassignError(null);
    try {
      await assignM.mutateAsync(vars);
    } catch (e) {
      setReassignError(mapMissionError(e));
      throw e; // keep the dialog open
    }
  }

  // SP-6f mapper + handler for "Ripristina lista" (state lifted to top to
  // respect React hook order).
  function mapResetSplitError(e: unknown): string {
    if (e instanceof LifecycleApiError) {
      if (e.code === 'pick_list_reset_blocked') {
        if (e.detail === 'active_mission_present') {
          return t('errors.reset_blocked_active');
        }
        if (e.detail === 'done_task_present') {
          return t('errors.reset_blocked_done_task');
        }
        return t('errors.reset_blocked_generic');
      }
      if (e.code === 'pick_list_invalid_state') {
        return t('errors.reset_invalid_state');
      }
      if (e.code === 'warehouse_admin_grant_required') {
        return t('errors.warehouse_admin_grant_required');
      }
      if (e.status === 404) return t('errors.pick_list_not_found');
    }
    return t('errors.reset_generic');
  }
  async function handleResetSplit(): Promise<void> {
    setResetSplitError(null);
    try {
      await resetSplitM.mutateAsync({ pickListId: id });
    } catch (e) {
      setResetSplitError(mapResetSplitError(e));
      throw e;
    }
  }

  const operators = boardQ.data?.operators ?? [];

  // SP-8d — map the assign-whole error (authedFetch throws Error{status,body})
  // to an Italian operator-facing message via the response body code.
  function mapAssignError(e: unknown): string {
    const body = (e as { body?: string })?.body ?? (e as Error)?.message ?? '';
    if (body.includes('operator_not_assignable')) return t('assign.errors.operator_not_assignable');
    if (body.includes('pick_list_already_split')) return t('assign.errors.already_split');
    if (body.includes('pick_list_not_open_for_assign')) return t('assign.errors.not_open');
    return t('assign.errors.unknown');
  }

  async function handleAssignWhole(operatorUserId: number): Promise<void> {
    setAssignError(null);
    try {
      await assignWholeM.mutateAsync({ pickListId: id, operatorUserId });
      setAssignOpen(false);
      refresh();
    } catch (e) {
      setAssignError(mapAssignError(e));
    }
  }

  // SP-8d — "Assegna lista" / "Split lista" inline: solo su liste `open`,
  // riservati a warehouse-admin / platform-admin (coerente col gate backend).
  const canAssignOrSplit = data.pick_list.status === 'open' && (isWarehouseAdmin || isPlatformAdmin);
  const customerActions = canAssignOrSplit ? (
    <>
      <Button type="button" color="info" outline size="sm"
              active={assignOpen}
              onClick={() => { setAssignError(null); setAssignOpen((v) => !v); }}>
        <i className="bi bi-person-check align-bottom me-1" aria-hidden="true" />
        {t('customers.assign_button')}
      </Button>
      <Button type="button" color="primary" size="sm" onClick={() => setWizardOpen(true)}>
        <i className="bi bi-diagram-3 align-bottom me-1" aria-hidden="true" />
        {t('customers.split_button')}
      </Button>
    </>
  ) : undefined;
  const customerPanel = canAssignOrSplit && assignOpen ? (
    <PickListAssignPanel
      pickListLabel={data.pick_list.external_id}
      operators={operators}
      pending={assignWholeM.isPending}
      error={assignError}
      onConfirm={handleAssignWhole}
      onClose={() => setAssignOpen(false)}
    />
  ) : undefined;

  async function handleConferma(colliImballo?: { colli: number; imballo: string }) {
    if (data == null) return;
    // I due valori si salvano PRIMA della conferma: sono la precondizione
    // all'invio verso Book. Se la conferma fallisse dopo, restano comunque
    // registrati — nessun dato perso e nessuna incoerenza.
    if (colliImballo != null) {
      try {
        await setColliImballoM.mutateAsync({ pickListId: id, ...colliImballo });
      } catch {
        // L'errore resta visibile nel dialog tramite lo stato della mutation;
        // non si prosegue con la conferma, altrimenti la lista risulterebbe
        // chiusa ma ferma.
        return;
      }
    }
    // evasioni-correzioni-parziale: sola conferma (niente note). `forcePartial`
    // è derivato dai parziali della lista → il backend accetta in un colpo solo.
    const forcePartial = pickListHasPartials(data.items);
    confirmM.mutate(
      { pickListId: id, notes: null, forcePartial },
      {
        onSuccess: async () => {
          setConfirmOpen(false); refresh();
          // SP-8 — conferma con parziali: il backend ha generato il CSV inevasi +
          // tentato l'email. Scarichiamo anche lato browser per immediatezza.
          if (forcePartial && token != null && tenantCode != null) {
            const ok = await downloadPartialsCsv(id, token, tenantCode, warehouseHint ?? null);
            setConfirmFlash({ kind: 'partial', csvDownloaded: ok });
          } else {
            setConfirmFlash({ kind: 'full', csvDownloaded: false });
          }
        },
        onError: (err) => { setConfirmOpen(false); console.error(err); },
      },
    );
  }

  function handleRiapri() {
    reopenM.mutate(
      { pickListId: id },
      {
        onSuccess: () => { setReopenOpen(false); refresh(); },
        onError: (err) => { setReopenOpen(false); console.error(err); },
      },
    );
  }

  return (
    <div>
      {confirmFlash != null && (
        <Alert
          color={confirmFlash.kind === 'partial' ? 'warning' : 'success'}
          isOpen
          toggle={() => setConfirmFlash(null)}
          className="mb-2"
        >
          {confirmFlash.kind === 'partial'
            ? confirmFlash.csvDownloaded
              ? t('postpicking.flash_partial_with_csv')
              : t('postpicking.flash_partial_no_csv')
            : t('postpicking.flash_full')}
        </Alert>
      )}
      <PickListSignoffBar
        status={data.pick_list.status}
        canMutate={canMutate}
        // SP-10: conferma manuale solo per liste con parziali (partial+cancelled);
        // le evase complete sono auto-inviate e auto-confermate dall'ERP.
        hasPartials={pickListHasPartials(data.items)}
        confirmedAt={data.pick_list.confirmed_at}
        confirmedByEmail={null}
        onConferma={() => setConfirmOpen(true)}
        onRiapri={() => setReopenOpen(true)}
      />
      {/* Evasione manuale: compare SOLO se non c'e' nulla da prelevare. */}
      <PickListManualFulfillBar
        pickListId={data.pick_list.id}
        status={data.pick_list.status}
        pickableRows={data.items.filter((i) => i.excluded_from_picking !== true).length}
        excludedRows={data.items.filter((i) => i.excluded_from_picking === true).length}
        canEdit={isWarehouseAdmin || isPlatformAdmin}
        warehouseIdHint={data.pick_list.warehouse_id}
      />
      <PickListColliImballoBar
        pickListId={data.pick_list.id}
        status={data.pick_list.status}
        colli={data.pick_list.colli ?? null}
        imballo={data.pick_list.imballo ?? null}
        exportedAt={data.pick_list.exported_at ?? null}
        canEdit={isWarehouseAdmin || isPlatformAdmin}
        warehouseIdHint={data.pick_list.warehouse_id}
      />
      {data.pick_list.auto_split_pending && data.pick_list.status === 'open' && (
        <PickListAutoSplitBanner onOpenWizard={() => setWizardOpen(true)} />
      )}
      {/* SP-6b order: clienti → articoli → missioni → audit (KPI cards removed).
          SP-7c: su mobile (< 768px) le 4 sezioni sono wrappate in sub-accordion
          per evitare uno scroll-page lunghissimo. Su desktop layout invariato. */}
      {isMobile ? (
        <Accordion open={openSection} toggle={(id) => setOpenSection((cur) => (cur === id ? '' : id))}>
          {hasCustomers && (
            <AccordionItem>
              <AccordionHeader targetId="customers">
                {t('detail.section_customers', { count: customers.length })}
              </AccordionHeader>
              <AccordionBody accordionId="customers">
                <PickListCustomerFilter
                  customers={customers}
                  selected={selectedCustomer}
                  onChange={setSelectedCustomer}
                  actions={customerActions}
                  panel={customerPanel}
                />
              </AccordionBody>
            </AccordionItem>
          )}
          <AccordionItem>
            <AccordionHeader targetId="items">
              {t('detail.section_items', { count: data.items.length })}
            </AccordionHeader>
            <AccordionBody accordionId="items">
              <PickListItemsSection
                items={data.items}
                customerKey={hasCustomers ? selectedCustomer : null}
                pickListStatus={data.pick_list.status}
                exportedAt={data.pick_list.exported_at ?? null}
                canEditPicked={isWarehouseAdmin || isPlatformAdmin}
                onEditPicked={async (pliId, qty) => { await editPickedM.mutateAsync({ pickListId: data.pick_list.id, pliId, qtyPicked: qty }); }}
              />
            </AccordionBody>
          </AccordionItem>
          <AccordionItem>
            <AccordionHeader targetId="missions">
              {t('detail.section_missions', { count: data.missions.length })}
            </AccordionHeader>
            <AccordionBody accordionId="missions">
              <PickListMissionsSection
                missions={data.missions}
                items={data.items}
                pickListStatus={data.pick_list.status}
                onOpenWizard={() => setWizardOpen(true)}
                operators={canMutate ? operators : undefined}
                onReassign={canMutate ? handleReassign : undefined}
                onUnassign={canMutate ? handleUnassign : undefined}
                onAssign={canMutate ? handleAssign : undefined}
                onCloseMission={canMutate ? handleCloseMission : undefined}
                onResetSplit={canMutate ? handleResetSplit : undefined}
                resetSplitError={resetSplitError}
                resetSplitPending={resetSplitM.isPending}
                pickListLabel={data.pick_list.external_id}
                reassignError={reassignError}
                reassignPending={reassignM.isPending || assignM.isPending}
              />
            </AccordionBody>
          </AccordionItem>
        </Accordion>
      ) : (
        <>
          {hasCustomers && (
            <PickListCustomerFilter
              customers={customers}
              selected={selectedCustomer}
              onChange={setSelectedCustomer}
              actions={customerActions}
              panel={customerPanel}
            />
          )}
          <PickListItemsSection
            items={data.items}
            customerKey={hasCustomers ? selectedCustomer : null}
            pickListStatus={data.pick_list.status}
            exportedAt={data.pick_list.exported_at ?? null}
            canEditPicked={canMutate}
            onEditPicked={async (pliId, qty) => { await editPickedM.mutateAsync({ pickListId: data.pick_list.id, pliId, qtyPicked: qty }); }}
          />
          <PickListMissionsSection
            missions={data.missions}
            items={data.items}
            pickListStatus={data.pick_list.status}
            onOpenWizard={() => setWizardOpen(true)}
            operators={canMutate ? operators : undefined}
            onReassign={canMutate ? handleReassign : undefined}
            onUnassign={canMutate ? handleUnassign : undefined}
            onAssign={canMutate ? handleAssign : undefined}
            onCloseMission={canMutate ? handleCloseMission : undefined}
            onResetSplit={canMutate ? handleResetSplit : undefined}
            resetSplitError={resetSplitError}
            resetSplitPending={resetSplitM.isPending}
            pickListLabel={data.pick_list.external_id}
            reassignError={reassignError}
            reassignPending={reassignM.isPending || assignM.isPending}
          />
        </>
      )}
      {wizardOpen && (
        <PickListSplitWizard
          isOpen={wizardOpen}
          onClose={() => setWizardOpen(false)}
          detail={data}
        />
      )}
      <ConfirmDialog
        isOpen={confirmOpen}
        pickListExternalId={data.pick_list.external_id}
        items={data.items}
        isPending={confirmM.isPending || setColliImballoM.isPending}
        colli={data.pick_list.colli ?? null}
        imballo={data.pick_list.imballo ?? null}
        onSubmit={(cb) => void handleConferma(cb)}
        onCancel={() => setConfirmOpen(false)}
      />
      <ReopenDialog
        isOpen={reopenOpen}
        pickListExternalId={data.pick_list.external_id}
        isPending={reopenM.isPending}
        onSubmit={handleRiapri}
        onCancel={() => setReopenOpen(false)}
      />
    </div>
  );
}
